import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Modal,
  Show
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  unitsApi,
  UNIT_INSTRUCTIONS_APPLY_KEY,
  UNIT_INSTRUCTION_TARGETS_KEY
} from "api/units";
import { useBulkUnitInstructionsProgress } from "providers/BulkUnitInstructionsProgress";
import type {
  InstructionTarget,
  InstructionTargetsResponse,
  InstructionType,
  Unit
} from "types/landlord/address";
import { InstructionTargetsList } from "./InstructionTargetsList";
import { InstructionTypeSelector } from "./InstructionTypeSelector";
import { OverwriteUnitInstructionsDialog } from "./OverwriteUnitInstructionsDialog";
import {
  getApplyErrorMessage,
  getUnitName,
  INSTRUCTION_TARGETS_PER_PAGE,
  INSTRUCTION_TYPES,
  isInstructionRunActive,
  isTargetOverridden,
  isTypeAvailable,
  MAX_INSTRUCTION_TARGETS,
  propertyLabel,
  unitLabel
} from "./helpers";

const SEARCH_DEBOUNCE_MS = 500;

type ApplyPayload =
  | { types: InstructionType[]; target_ids: number[] }
  | {
      types: InstructionType[];
      apply_to_all: true;
      search?: string;
      excluded_ids?: number[];
    };

const countDistinctProperties = (targets: InstructionTarget[]) =>
  new Set(targets.map((target) => target.property_id)).size;

export const ApplyUnitInstructionsModal = ({
  unit,
  initialType,
  onClose
}: {
  unit: Unit;
  initialType: InstructionType;
  onClose: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const {
    startTracking,
    run: activeRun,
    status: activeRunStatus
  } = useBulkUnitInstructionsProgress();
  const isBlockedByActiveRun = isInstructionRunActive(
    activeRun,
    activeRunStatus
  );

  const [selectedTypes, setSelectedTypes] = useState<InstructionType[]>(() =>
    isTypeAvailable(unit.instructions, initialType) ? [initialType] : []
  );
  const [selected, setSelected] = useState<Map<number, InstructionTarget>>(
    () => new Map()
  );
  const [applyMode, setApplyMode] = useState<"explicit" | "all">("explicit");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [previousSearch, setPreviousSearch] = useState(debouncedSearch);
  if (previousSearch !== debouncedSearch) {
    setPreviousSearch(debouncedSearch);
    setSelected(new Map());
    setApplyMode("explicit");
    setExcludedIds(new Set());
  }

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<InstructionTargetsResponse>({
    queryKey: [
      UNIT_INSTRUCTION_TARGETS_KEY,
      unit.id,
      debouncedSearch,
      INSTRUCTION_TARGETS_PER_PAGE
    ],
    queryFn: ({ pageParam }) =>
      unitsApi.getInstructionTargets(unit.id, {
        page: Number(pageParam),
        per_page: INSTRUCTION_TARGETS_PER_PAGE,
        search: debouncedSearch.trim() || undefined
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined
  });

  const targets = useMemo<InstructionTarget[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const targetById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets]
  );

  const selectedFresh = useMemo(
    () =>
      Array.from(selected.values()).map(
        (target) => targetById.get(target.id) ?? target
      ),
    [selected, targetById]
  );

  const overwriteTargets = useMemo(
    () =>
      selectedFresh.filter((target) =>
        isTargetOverridden(target, selectedTypes)
      ),
    [selectedFresh, selectedTypes]
  );

  const isAllMode = applyMode === "all";
  const extra = data?.pages[0]?.extra;
  const totalMatchingCount = data?.pages[0]?.total_count ?? 0;

  const allModeOverwriteCountByType = useMemo(
    () => ({
      entry: extra?.entry_override_count ?? 0,
      exit: extra?.exit_override_count ?? 0
    }),
    [extra]
  );

  const checkedIds = useMemo(() => {
    if (!isAllMode) return selected;

    const map = new Map<number, InstructionTarget>();
    targets.forEach((target) => {
      if (target.pms_locked) return;
      if (!excludedIds.has(target.id)) map.set(target.id, target);
    });

    return map;
  }, [isAllMode, selected, targets, excludedIds]);

  const selectedCount = isAllMode
    ? Math.max(totalMatchingCount - excludedIds.size, 0)
    : selected.size;
  const selectedPropertyCount = isAllMode
    ? countDistinctProperties(targets)
    : countDistinctProperties(selectedFresh);
  const isOverCap = !isAllMode && selected.size > MAX_INSTRUCTION_TARGETS;
  const hasSelectedTypes = selectedTypes.length > 0;

  const closeAndReset = () => {
    setSelected(new Map());
    setApplyMode("explicit");
    setExcludedIds(new Set());
    setSearch("");
    setIsConfirmOpen(false);
    onClose();
  };

  const { mutate: applyInstructions, isPending: isEnqueuing } = useMutation({
    mutationKey: [UNIT_INSTRUCTIONS_APPLY_KEY, "CREATE"],
    mutationFn: (payload: ApplyPayload) =>
      unitsApi.applyInstructions(unit.id, payload),
    onSuccess: (response) => {
      startTracking({ sourceUnitId: unit.id, bulkApplyId: response.id });
      closeAndReset();
    },
    onError: (mutationError) => {
      setIsConfirmOpen(false);
      toast.error(
        `Failed to apply the instructions: ${getApplyErrorMessage(mutationError, dictionary)}`
      );
    }
  });

  const handleClose = () => {
    if (isEnqueuing) return;

    closeAndReset();
  };

  const handleConfirmClose = () => {
    if (isEnqueuing) return;

    setIsConfirmOpen(false);
  };

  const toggleType = (type: InstructionType) =>
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((entry) => entry !== type)
        : INSTRUCTION_TYPES.filter(
            (entry) => entry === type || prev.includes(entry)
          )
    );

  const toggleTarget = (target: InstructionTarget) => {
    if (target.pms_locked) return;

    if (isAllMode) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(target.id)) next.delete(target.id);
        else next.add(target.id);

        return next;
      });
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(target.id)) next.delete(target.id);
      else next.set(target.id, target);

      return next;
    });
  };

  const toggleProperty = (
    propertyTargets: InstructionTarget[],
    nextChecked: boolean
  ) => {
    if (isAllMode) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        propertyTargets.forEach((target) => {
          if (nextChecked) next.delete(target.id);
          else next.add(target.id);
        });

        return next;
      });
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      propertyTargets.forEach((target) => {
        if (nextChecked) next.set(target.id, target);
        else next.delete(target.id);
      });

      return next;
    });
  };

  const handleSelectAllClick = () => {
    setSelected(new Map());
    setExcludedIds(new Set());
    setApplyMode(isAllMode ? "explicit" : "all");
  };

  const runApply = () => {
    if (isAllMode) {
      applyInstructions({
        types: selectedTypes,
        apply_to_all: true,
        search: debouncedSearch.trim() || undefined,
        excluded_ids: Array.from(excludedIds)
      });
      return;
    }

    applyInstructions({
      types: selectedTypes,
      target_ids: Array.from(selected.keys())
    });
  };

  const needsConfirmation = isAllMode
    ? selectedTypes.some((type) => allModeOverwriteCountByType[type] > 0)
    : overwriteTargets.length > 0;

  const handleApplyClick = () => {
    if (needsConfirmation) {
      setIsConfirmOpen(true);
      return;
    }

    runApply();
  };

  const sourceName = getUnitName({ name: unit.title }, dictionary);

  return (
    <>
      <Modal
        onClose={handleClose}
        opened={!isConfirmOpen}
        panelClassName="flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden"
      >
        <header className="shrink-0 border-b border-alpha-40 p-6">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-bold text-content-1000">
              Apply to other {dictionary.units}
            </h3>
            <IconButton
              HugeIcon={Cancel01Icon}
              disabled={isEnqueuing}
              onClick={handleClose}
              size={IconButtonSize.LG}
              variant={IconButtonVariant.TEXT_BASE}
            />
          </div>
          <p className="mt-2 text-sm text-content-400">
            The instructions saved on {sourceName} will be applied to the{" "}
            {dictionary.units} you select below.
          </p>
        </header>

        <div className="shrink-0 space-y-2 border-b border-alpha-40 px-6 py-4">
          <p className="text-xs font-medium text-content-400">
            Instructions to apply
          </p>
          <InstructionTypeSelector
            instructions={unit.instructions}
            onToggleType={toggleType}
            selectedTypes={selectedTypes}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col space-y-2 px-6 py-4">
          <p className="shrink-0 text-xs font-medium text-content-400">
            {dictionary.Units}
          </p>

          <div className="flex shrink-0 items-center justify-between gap-4">
            <BaseInput
              className="w-full max-w-[293px]"
              id="instruction-target-search"
              onChange={setSearch}
              placeholder="Search"
              size={InputSize.SM}
              type="search"
              value={search}
            />
            <Button
              className="shrink-0"
              disabled={totalMatchingCount === 0}
              onClick={handleSelectAllClick}
              variant={ButtonVariant.TEXT}
            >
              {isAllMode ? "Deselect all" : "Select all"}
            </Button>
          </div>

          <Show if={isAllMode}>
            <p className="shrink-0 text-xs text-content-300">
              Applying to every {dictionary.unit} matching your search — uncheck
              any you want to skip. This replaces existing instructions on
              affected {dictionary.units}.
            </p>
          </Show>

          <div className="-mx-3 !mt-6 flex min-h-0 flex-1 flex-col">
            <InstructionTargetsList
              error={error}
              hasNextPage={hasNextPage}
              hasSearch={Boolean(debouncedSearch.trim())}
              isError={isError}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              onLoadMore={fetchNextPage}
              onToggleProperty={toggleProperty}
              onToggleTarget={toggleTarget}
              selectedIds={checkedIds}
              selectedTypes={selectedTypes}
              visibleTargets={targets}
            />
          </div>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-4 border-t border-alpha-40 px-6 py-4">
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-content-400">
              {selectedCount} {unitLabel(selectedCount, dictionary)} selected
              across {selectedPropertyCount}{" "}
              {propertyLabel(selectedPropertyCount, dictionary)}
            </p>
            <Show if={!hasSelectedTypes}>
              <p className="text-sm font-medium text-warning-600">
                Select at least one set of instructions to apply.
              </p>
            </Show>
            <Show if={isOverCap}>
              <p className="text-sm font-medium text-danger-500">
                You can apply to at most {MAX_INSTRUCTION_TARGETS}{" "}
                {dictionary.units} at a time. Use “Select all” to apply to
                everything matching your search instead.
              </p>
            </Show>
            <Show if={isBlockedByActiveRun}>
              <p className="text-sm font-medium text-warning-600">
                A previous bulk apply run is still in progress — wait for it to
                finish or cancel it (top-right) before starting another.
              </p>
            </Show>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button disabled={isEnqueuing} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={
                selectedCount === 0 ||
                !hasSelectedTypes ||
                isOverCap ||
                isBlockedByActiveRun
              }
              loading={isEnqueuing}
              onClick={handleApplyClick}
              variant={ButtonVariant.PRIMARY}
            >
              Apply
            </Button>
          </div>
        </footer>
      </Modal>

      <OverwriteUnitInstructionsDialog
        allModeOverwriteCountByType={allModeOverwriteCountByType}
        canClose={!isEnqueuing}
        isAllMode={isAllMode}
        isPending={isEnqueuing}
        onClose={handleConfirmClose}
        onConfirm={runApply}
        opened={isConfirmOpen}
        overwriteTargets={overwriteTargets}
        selectedCount={selectedCount}
        selectedTypes={selectedTypes}
        sourceName={sourceName}
      />
    </>
  );
};
